// Ce que le menu montre, pour toute l'entreprise.
//
// Le menu comptait vingt entrées, dont des modules jamais utilisés : autant de
// choix à lire à chaque fois qu'on cherche un écran. L'administrateur coche ce
// qui sert ; le reste disparaît du menu de tout le monde. Masquer n'interdit
// rien : l'écran reste ouvert à qui en a le droit et le lien, et un clic le
// fait revenir.

import { MODULES_TOUJOURS_VISIBLES } from '@imp/shared/menu-entreprise';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CLE_MENU, getModulesMasques, setModulesMasques } from '@/lib/menu-api';
import { GROUPES, MODULES } from '@/lib/modules';

/** Ce que l'administrateur peut retirer du menu. */
const MASQUABLES = MODULES.filter((m) => !m.horsMenu && !MODULES_TOUJOURS_VISIBLES.includes(m.id));

function memeListe(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

export function MenuEntrepriseCard() {
  const qc = useQueryClient();
  const masquesQ = useQuery({ queryKey: CLE_MENU, queryFn: getModulesMasques });
  const [masques, setMasques] = useState<string[] | null>(null);

  // Le formulaire part de ce qui est enregistré, une fois lu.
  useEffect(() => {
    if (masquesQ.data && masques === null) setMasques(masquesQ.data);
  }, [masquesQ.data, masques]);

  const enregistrer = useMutation({
    mutationFn: (liste: string[]) => setModulesMasques(liste),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLE_MENU });
    },
  });

  const modifie =
    masques !== null && masquesQ.data !== undefined && !memeListe(masques, masquesQ.data);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Menu</CardTitle>
        <p className="text-muted-foreground text-sm">
          Les modules cochés apparaissent dans le menu de tout le monde. Décochez ceux que
          l&apos;entreprise n&apos;utilise pas : ils disparaissent du menu, mais rien n&apos;est
          supprimé, et chacun garde ses droits. Les alertes s&apos;ouvrent depuis le tableau de
          bord, l&apos;assistant depuis son bouton en haut de l&apos;écran.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {masquesQ.isError ? (
          <p className="text-destructive text-sm">
            Impossible de lire les réglages du menu pour le moment : rien n&apos;a été modifié.
          </p>
        ) : masques === null ? (
          <p className="text-muted-foreground text-sm">Chargement…</p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {GROUPES.map((g) => {
                const liste = MASQUABLES.filter((m) => m.groupe === g.id);
                if (liste.length === 0) return null;
                return (
                  <fieldset key={g.id} className="space-y-1.5">
                    <legend className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                      {g.label}
                    </legend>
                    {liste.map((m) => {
                      const Icon = m.icon;
                      const visible = !masques.includes(m.id);
                      return (
                        <label
                          key={m.id}
                          className="flex min-h-9 cursor-pointer items-center gap-2.5 text-sm"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={visible}
                            onChange={(e) => {
                              setMasques(
                                e.target.checked
                                  ? masques.filter((x) => x !== m.id)
                                  : [...masques, m.id],
                              );
                            }}
                          />
                          <Icon className="text-muted-foreground h-4 w-4" />
                          <span className={visible ? '' : 'text-muted-foreground line-through'}>
                            {m.label}
                          </span>
                        </label>
                      );
                    })}
                  </fieldset>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={!modifie || enregistrer.isPending}
                onClick={() => {
                  enregistrer.mutate(masques);
                }}
              >
                {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer le menu'}
              </Button>
              {modifie ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMasques(masquesQ.data);
                  }}
                >
                  Annuler
                </Button>
              ) : null}
              {enregistrer.isError ? (
                <span className="text-destructive text-sm">{messageErreur(enregistrer.error)}</span>
              ) : enregistrer.isSuccess && !modifie ? (
                <span className="text-money-in text-sm">Menu enregistré.</span>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
